import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const from = (location.state as any)?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { user: loggedInUser } = await login(email, password);
      
      // Validate user role - only CUSTOMER role can login from frontend
      if (loggedInUser && loggedInUser.role !== 'CUSTOMER') {
        setError('This account is not authorized to access the customer portal. Please use the yoga school admin dashboard.');
        return;
      }
      
      // Check if email is verified - if not, redirect to verification page
      if (loggedInUser && !loggedInUser.emailVerified) {
        navigate('/verify-email', { 
          replace: true,
          state: { email: loggedInUser.email }
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      const status = err?.response?.status;
      const backendError =
        (typeof err?.response?.data?.error === 'object'
          ? err?.response?.data?.error?.message
          : err?.response?.data?.error) ||
        err?.response?.data?.message ||
        err?.message;

      if (status === 401) {
        setError('Invalid email or password. Please try again.');
      } else if (status === 403) {
        setError('This account is not authorized to access the customer portal.');
      } else {
        setError(backendError || 'Login failed. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Login - Book My Yoga TTC"
        description="Sign in to your account to book consultations and access exclusive features."
        url="https://bookmyyogattc.com/login"
      />
      <div className="pt-20 min-h-screen flex">
        {/* Left Side - Visual */}
        <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary via-primary-dark to-primary relative overflow-hidden">
          {/* Background Image Overlay */}
          <div className="absolute inset-0">
            <img 
              src="/images/cover.png" 
              alt="Yoga" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary-dark/80 to-primary/90"></div>
          </div>
          
          {/* Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="yogaPattern" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="white" strokeWidth="1"/>
                  <circle cx="60" cy="60" r="35" fill="none" stroke="white" strokeWidth="0.5"/>
                  <circle cx="60" cy="60" r="20" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#yogaPattern)" />
            </svg>
          </div>
          
          <div className="relative z-10 flex flex-col items-center justify-center p-12 text-white w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-md"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity }}
                className="text-5 mb-6"
              >
                🧘‍♀️
              </motion.div>
              <h2 className="text-5 font-semibold mb-4">Welcome Back</h2>
              <p className="text-4 opacity-95 leading-relaxed">
                Continue your journey towards inner peace and transformation. Access exclusive resources and connect with certified yoga schools.
              </p>
              
              {/* Features List */}
              <div className="mt-8 space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Book consultations instantly</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Access exclusive school listings</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-5">✓</span>
                  <span className="opacity-90">Track your applications</span>
                </div>
              </div>
            </motion.div>
            
            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -30, 0], rotate: [0, 10, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
              className="absolute top-20 left-20 text-5 opacity-15"
            >
              🕉️
            </motion.div>
            <motion.div
              animate={{ y: [0, 30, 0], rotate: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, delay: 1 }}
              className="absolute bottom-20 right-20 text-5 opacity-15"
            >
              🪷
            </motion.div>
            <motion.div
              animate={{ y: [0, -25, 0] }}
              transition={{ duration: 5, repeat: Infinity, delay: 0.5 }}
              className="absolute top-1/2 right-10 text-5 opacity-10"
            >
              ☯️
            </motion.div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center bg-white p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-md"
          >
            <div className="mb-6">
              <h1 className="text-5 font-semibold text-ivory-900 mb-2">Sign In</h1>
              <p className="text-ivory-600">Access your account to continue</p>
            </div>

            <div className="bg-white rounded-xl shadow-xl p-8 border border-ivory-100">
              <form
                className="space-y-5"
                onSubmit={handleSubmit}
              >
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-2">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-ivory-700 font-medium mb-2">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-ivory-700 font-medium mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-ivory-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors bg-white text-ivory-900 placeholder-ivory-400"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center hover:opacity-70 transition-opacity"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5 text-ivory-400" />
                      ) : (
                        <Eye className="h-5 w-5 text-ivory-400" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <Link to="/forgot-password" className="text-2 font-medium text-primary hover:text-primary-dark">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-6 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Sign In'
                  )}
                </button>

                <div className="text-center pt-4 border-t border-ivory-200">
                  <p className="text-2 text-ivory-600">
                    Don't have an account?{' '}
                    <Link to="/signup" className="font-semibold text-primary hover:text-primary-dark">
                      Sign up
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Login;

